// Taking a vendor out of service, and putting it back (ff-api#1232).
import React, { useState } from 'react';
import { Loader2, Power, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useSetEnabledMutation } from '@/client/api/integrations/integrations.query';
import { Integration } from '@/client/types/integrations';

/** What the vendor's state actually is, and whether an admin can change it. */
export const enableState = (row: Integration) => {
  const envEnabled = row.flag_state?.env_enabled ?? true;
  const override = row.enabled_override ?? row.flag_state?.override ?? null;
  return {
    envEnabled,
    override,
    // An override can only disable; env off is a hard off on the other side too.
    effective: envEnabled && override !== false,
    envDisabled: !envEnabled,
  };
};

const EnableControl: React.FC<{ row: Integration; canManage: boolean }> = ({ row, canManage }) => {
  const { envEnabled, override, effective, envDisabled } = enableState(row);
  const [confirming, setConfirming] = useState(false);
  const setEnabled = useSetEnabledMutation();

  const apply = async (enabled: boolean | null) => {
    await setEnabled.mutateAsync({ name: row.name, enabled });
    setConfirming(false);
  };

  if (envDisabled) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        <div>
          <p className="font-medium text-gray-700">Disabled for this environment</p>
          <p className="text-gray-500">
            {row.enable_flag ? <code className="text-xs">{row.enable_flag}</code> : 'Its env flag'}{' '}
            is off, which usually means there are no credentials for it here. Changing that takes a
            keystore edit and a redeploy — it can&apos;t be switched on from the admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
            effective ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <Power className="h-3.5 w-3.5" />
          {effective ? 'In service' : 'Taken out of service'}
        </span>
        {override === false && (
          <span className="text-xs text-gray-500">switched off by an admin, not by config</span>
        )}
        {!canManage && (
          <span className="text-xs text-gray-400">needs the integration.manage permission</span>
        )}
      </div>

      {canManage && !confirming && (
        <Button
          size="sm"
          variant={effective ? 'outline' : 'default'}
          onClick={() => (effective ? setConfirming(true) : apply(null))}
          disabled={setEnabled.isPending}
        >
          {setEnabled.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {effective ? 'Take out of service' : 'Put back in service'}
        </Button>
      )}

      {canManage && confirming && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="text-amber-800">
            Every call to {row.name} will fail while it is out of service, in{' '}
            <span className="font-medium">this environment only</span>. It takes effect within about
            30 seconds, and no deploy is involved either way.
          </p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="destructive" onClick={() => apply(false)} disabled={setEnabled.isPending}>
              {setEnabled.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Take {row.name} out of service
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {setEnabled.isError && (
        <p className="text-sm text-red-600">{getApiErrorMessage(setEnabled.error)}</p>
      )}
    </div>
  );
};

export default EnableControl;
